// Query Key Generator for TanStack Query (Vue Query)
// Provides type-safe and consistent query key generation

import { PaginatedTanstackQueryParams } from '@/lib/types/v1'
import { Route } from '@/composables/route'
import { isEmpty } from '@/lib/utils'
import { CompanyAssetRouteName } from '@/views/company/asset/routes'
import { CompanySecretRouteName } from '@/views/company/secret/routes'
import { CompanyContactRouteName } from '@/views/company/contact/routes'
import { TicketRouteName } from '@/modules/ticket/routes'
import { CompanyPayRouteName } from '@/views/company/pay/routes'

export type QueryKeyParams = Record<string, any>

/**
 * Base query key generator class
 */
export class QueryKeyGenerator {
    private readonly baseKey: string[]

    constructor(baseKey: string | string[]) {
        this.baseKey = Array.isArray(baseKey) ? baseKey : [baseKey]
    }

    /**
     * Generate a query key with optional parameters
     */
    key(params?: QueryKeyParams): string[] {
        if (!params || Object.keys(params).length === 0) {
            return [...this.baseKey]
        }

        // Sort params for consistent key generation
        const sortedParams = Object.keys(params)
            .sort()
            .reduce((acc, key) => {
                acc[key] = isEmpty(params[key]) ? null : params[key]
                return acc
            }, {} as QueryKeyParams)

        return [...this.baseKey, sortedParams]
    }

    all(): string[] {
        return [...this.baseKey, 'all']
    }

    /**
     * Generate a query key for a specific item by ID
     */
    item(id: string | number): string[] {
        return [...this.baseKey, { id }]
    }

    /**
     * Generate a query key for a list with optional filters
     */
    list(route: Route): string[] {
        const companyRoutes = [CompanyContactRouteName.Index, CompanyPayRouteName.Index]
        const params = {
            q: isEmpty(route.q.value) ? null : route.q.value,
            limit: route.limit.value ?? null,
            page: route.pageIndex.value ?? null,
            sort: isEmpty(route.sort.value) ? null : route.sort.value,
            filter: isEmpty(route.filters.value) ? null : route.filters.value,
            groupBy: route.groupBy.value ?? null,
            view: route.view.value,
            ...(route.name.value === CompanyAssetRouteName.Secrets && {
                owner_uuid: route.companyAssetUuid.value,
            }),
            ...(route.name.value === CompanySecretRouteName.Index && {
                owner_uuid: route.companyUuid.value,
            }),
            ...(companyRoutes.includes(route.name.value) && {
                company_uuid: route.companyUuid.value,
            }),
            ...(route.name.value === TicketRouteName.Details && {
                ticket_uuid: route.ticketUuid.value,
            }),
        }

        return this.key(params)
    }

    /**
     * Generate a query key for infinite queries
     */
    infinite(params?: QueryKeyParams): string[] {
        return this.key({ type: 'infinite', ...params })
    }

    fromPaginatedTanstackQueryParams(params: PaginatedTanstackQueryParams) {
        return this.key({
            q: params.q,
            limit: params.limit,
            page: params.pageIndex ?? null,
            sort: JSON.stringify(params.sort),
            filter: JSON.stringify(params.filter),
            groupBy: params.groupBy ?? null,
            view: params.view,
            companyUuid: params.companyUuid,
        })
    }
}

/**
 * Factory function to create query key generators
 */
export function createQueryKeys(baseKey: string | string[]) {
    return new QueryKeyGenerator(baseKey)
}

// Predefined query key generators for common entities
export const queryKeys = {
    articles: createQueryKeys(['v1', 'articles']),
    cloudflareAccounts: createQueryKeys(['v1', 'cloudflare-accounts']),
    cloudflareDnsRecords: createQueryKeys(['v1', 'cloudflare-dns-records']),
    cloudflareServers: createQueryKeys(['v1', 'cloudflare-servers']),
    cloudflareZones: createQueryKeys(['v1', 'cloudflare-zones']),
    companies: createQueryKeys(['v1', 'companies']),
    companyTypes: createQueryKeys(['v1', 'company-types']),
    companyAssets: createQueryKeys(['v1', 'company-assets']),
    companyContacts: createQueryKeys(['v1', 'company-contacts']),
    companyLocations: createQueryKeys(['v1', 'company-locations']),
    companyServices: createQueryKeys(['v1', 'company-services']),
    conversations: createQueryKeys(['v1', 'conversations']),
    conversationReplies: createQueryKeys(['v1', 'conversation-replies']),
    datums: createQueryKeys(['v1', 'datums']),
    invoices: createQueryKeys(['v1', 'invoices']),
    invoiceItems: createQueryKeys(['v1', 'invoice-items']),
    logs: createQueryKeys(['v1', 'logs']),
    media: createQueryKeys(['v1', 'media']),
    microsoftUsers: createQueryKeys(['v1', 'microsoft-users']),
    payments: createQueryKeys(['v1', 'payments']),
    rates: createQueryKeys(['v1', 'rates']),
    roles: createQueryKeys(['v1', 'roles']),
    secrets: createQueryKeys(['v1', 'secrets']),
    staff: createQueryKeys(['v1', 'staff']),
    serviceGroups: createQueryKeys(['v1', 'service-groups']),
    statements: createQueryKeys(['v1', 'statements']),
    tariffs: createQueryKeys(['v1', 'tariffs']),
    tariffItems: createQueryKeys(['v1', 'tariff-items']),
    taxRates: createQueryKeys(['v1', 'tax-rates']),
    tenants: createQueryKeys(['v1', 'tenants']),
    tickets: createQueryKeys(['v1', 'tickets']),
    ticketDepartments: createQueryKeys(['v1', 'ticket', 'departments']),
    ticketPriorities: createQueryKeys(['v1', 'ticket', 'priorities']),
    ticketResources: createQueryKeys(['v1', 'ticket', 'resources']),
    ticketStatuses: createQueryKeys(['v1', 'ticket', 'statuses']),
    ticketTypes: createQueryKeys(['v1', 'ticket', 'types']),
    timezones: createQueryKeys(['v1', 'timezones']),
    user: createQueryKeys(['v1', 'user']),
}

// Advanced query key factory with nested relationships
export class AdvancedQueryKeyFactory {
    /**
     * Generate keys for nested resources (e.g., user's posts)
     */
    static nested(
        parent: string,
        parentId: string | number,
        child: string,
        params?: QueryKeyParams
    ): string[] {
        const base = [parent, parentId, child]
        return params ? [...base, params] : base
    }

    /**
     * Generate keys for related resources
     */
    static related(baseResource: string, relation: string, params?: QueryKeyParams): string[] {
        const base = [baseResource, 'related', relation]
        return params ? [...base, params] : base
    }

    /**
     * Generate keys for search queries
     */
    static search(resource: string, query: string, filters?: QueryKeyParams): string[] {
        return [resource, 'search', { query, ...filters }]
    }

    /**
     * Generate keys for aggregated data
     */
    static aggregate(resource: string, aggregationType: string, params?: QueryKeyParams): string[] {
        return [resource, 'aggregate', aggregationType, params].filter(Boolean)
    }
}

// Example usage patterns
export const exampleUsage = {
    // Advanced patterns
    userPosts: AdvancedQueryKeyFactory.nested('users', 123, 'posts'),
    userPostsPaginated: AdvancedQueryKeyFactory.nested('users', 123, 'posts', { page: 1 }),
    postComments: AdvancedQueryKeyFactory.nested('posts', 456, 'comments'),

    // Search
    searchUsers: AdvancedQueryKeyFactory.search('users', 'john doe', { role: 'admin' }),

    // Aggregations
    userStats: AdvancedQueryKeyFactory.aggregate('users', 'stats'),
    postMetrics: AdvancedQueryKeyFactory.aggregate('posts', 'metrics', { period: 'month' }),
}

// Vue composable example
export function useQueryKeys() {
    return {
        queryKeys,
        AdvancedQueryKeyFactory,
        createQueryKeys,
    }
}

// Type-safe query key builder
export type QueryKeyBuilder<T extends string> = {
    [K in T]: QueryKeyGenerator
}

export function createTypedQueryKeys<T extends string>(keys: T[]): QueryKeyBuilder<T> {
    return keys.reduce((acc, key) => {
        acc[key] = createQueryKeys([key])
        return acc
    }, {} as any)
}

// Example of typed query keys
const typedKeys = createTypedQueryKeys(['users', 'posts', 'comments'] as const)
// typedKeys.users.key() - fully typed
// typedKeys.posts.item(123) - fully typed

export { typedKeys as exampleTypedKeys }
